import React from 'react'
import { Helmet } from 'react-helmet'
import utils from '../utils'
import {
    FaClock,
    FaBolt,
    FaStethoscope,
    FaArrowRight,
    FaHeartbeat,
    FaCheckCircle
} from 'react-icons/fa';
import { useAdminData } from '../context/SessionContext';
import { MdVerified } from 'react-icons/md';
import { Link } from 'react-router-dom';

function ServicesPage(props) {
    const { services, categories } = useAdminData();
    const url = window.location.href

    const getCategoryStyle = (category) => {
        if (category.is_shift) {
            return {
                icon: FaClock,
                bg: 'bg-primary/20',
                text: 'text-primary',
                border: 'hover:border-primary/30',
                accent: 'primary'
            };
        }
        if (category.name.includes('Short')) {
            return {
                icon: FaBolt,
                bg: 'bg-accent/20',
                text: 'text-accent',
                border: 'hover:border-accent/30',
                accent: 'accent'
            };
        }
        return {
            icon: FaStethoscope,
            bg: 'bg-success/20',
            text: 'text-success',
            border: 'hover:border-success/30',
            accent: 'success'
        };
    };

    const formatPrice = (price) => {
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: 'KES',
            minimumFractionDigits: 0
        }).format(price);
    };
    return (
        <div>
            <Helmet>
                <title>Services - {utils.siteName}</title>
                <link rel="canonical" href={url} />
                <meta
                    name="description"
                    content="Browse verified home healthcare services in Kenya. From nursing shifts to medical procedures like IV infusion, injections, and wound care. Book certified nurses at home."
                />
                <meta
                    property="og:title"
                    content="Our Services - HealthConnect | Home Healthcare Kenya"
                />
                <meta
                    property="og:description"
                    content="Professional nursing care, medical procedures, and health services delivered at your doorstep. Verified nurses, transparent pricing, 24/7 availability."
                />
                <meta
                    property="og:type"
                    content="website"
                />
                <meta
                    property="og:url"
                    content={url}
                />
                <meta
                    property="og:image"
                    content="https://healthconnect.co.ke/images/og-services.jpg"
                />
                <meta
                    name="twitter:card"
                    content="summary_large_image"
                />
                <meta
                    name="twitter:title"
                    content="HealthConnect Services | Book Nurses at Home"
                />
                <meta
                    name="twitter:description"
                    content="Shift nursing, IV drips, injections, dressing, and more. Certified professionals, hospital-grade care at home."
                />
                <meta
                    name="keywords"
                    content="home nursing Kenya, nurse booking, IV infusion at home, injection service, wound dressing, stitch removal, medical procedures Nairobi, HealthConnect services, certified nurses Kenya"
                />
                <meta
                    name="robots"
                    content="index, follow"
                />
            </Helmet>
            <section className="px-6 md:px-12 py-5 bg-base-100 relative overflow-hidden -mt-4">
                <div className="absolute -top-24 -right-24 size-96 bg-primary/10 rounded-full blur-3xl"></div>
                <div className="absolute -bottom-24 -left-24 size-96 bg-blue-400/10 rounded-full blur-3xl"></div>

                <div className="max-w-5xl mx-auto relative">
                    <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-bold uppercase tracking-wider mb-6">
                        <span className="size-2 rounded-full bg-primary mr-2 animate-pulse"></span>
                        Our Services
                    </span>

                    <h2 className="text-3xl md:text-5xl font-black text-text-main dark:text-white leading-[1.1] tracking-tight mb-8">
                        Professional Care <span className="text-primary">When You Need It</span>
                    </h2>

                    <p className="text-lg text-text-secondary dark:text-slate-400 font-light max-w-2xl leading-relaxed">
                        Browse our range of verified healthcare services, from quick procedures to extended nursing care, all delivered at home by certified professionals.
                    </p>
                </div>
            </section>

            <section className="py-16 px-6 bg-base-100">
                <div className="">
                    <div className="grid md:grid-cols-3 gap-6">
                        <div className="group bg-white dark:bg-slate-900 p-6 rounded-2xl border border-border-color dark:border-slate-800 shadow-sm hover:shadow-card-hover hover:border-primary/30 transition-all duration-300">
                            <div className="size-14 rounded-2xl bg-primary/20 text-primary flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                                <MdVerified className="text-3xl" />
                            </div>
                            <h3 className="text-xl font-bold text-text-main dark:text-white mb-3">Verified Professionals</h3>
                            <p className="text-secondary dark:text-slate-400 leading-relaxed">
                                Every nurse is background-checked, licensed, and trained to deliver hospital-grade care in your home.
                            </p>
                        </div>

                        <div className="group bg-white dark:bg-slate-900 p-6 rounded-2xl border border-border-color dark:border-slate-800 shadow-sm hover:shadow-card-hover hover:border-primary/30 transition-all duration-300">
                            <div className="size-14 rounded-2xl bg-primary/20 text-primary flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                                <FaCheckCircle className="text-3xl" />
                            </div>
                            <h3 className="text-xl font-bold text-text-main dark:text-white mb-3">Transparent Pricing</h3>
                            <p className="text-secondary dark:text-slate-400 leading-relaxed">
                                No hidden fees. Know exactly what you're paying for with clear, upfront pricing for every service.
                            </p>
                        </div>

                        <div className="group bg-white dark:bg-slate-900 p-6 rounded-2xl border border-border-color dark:border-slate-800 shadow-sm hover:shadow-card-hover hover:border-primary/30 transition-all duration-300">
                            <div className="size-14 rounded-2xl bg-primary/20 text-primary flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                                <FaHeartbeat className="text-3xl" />
                            </div>
                            <h3 className="text-xl font-bold text-text-main dark:text-white mb-3">Available 24/7</h3>
                            <p className="text-secondary dark:text-slate-400 leading-relaxed">
                                Emergency or scheduled care, we're here around the clock to support your health needs.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-16 px-6 bg-base-200/50">
                <div className="">
                    <div className="text-center mb-12">
                        <h2 className="text-2xl md:text-3xl font-black text-text-main dark:text-white mb-4">
                            Our <span className="text-primary">Categories</span>
                        </h2>
                        <p className="text-lg text-secondary dark:text-slate-400 max-w-2xl mx-auto">
                            We organize our services into clear categories to help you find the right care quickly.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6">
                        {categories?.map((category) => {
                            const style = getCategoryStyle(category);
                            const Icon = style.icon;

                            return (
                                <div
                                    key={category.id}
                                    className={`group bg-white dark:bg-slate-900 p-6 rounded-2xl border border-border-color dark:border-slate-800 shadow-sm ${style.border} transition-all duration-300 hover:-translate-y-1`}
                                >
                                    <div className="flex items-start justify-between mb-4">
                                        <div className={`size-14 rounded-2xl ${style.bg} ${style.text} flex items-center justify-center group-hover:scale-110 transition-transform`}>
                                            <Icon className="text-3xl" />
                                        </div>
                                        <span className="text-xs font-bold px-3 py-1 rounded-full bg-base-200 text-secondary">
                                            {category.service_count} {category.service_count === 1 ? 'Service' : 'Services'}
                                        </span>
                                    </div>

                                    <h3 className="text-xl font-bold text-text-main dark:text-white mb-3">
                                        {category.name}
                                    </h3>
                                    <p className="text-secondary dark:text-slate-400 text-sm leading-relaxed">
                                        {category.description}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            <section className="py-16 px-6 bg-base-100">
                <div className="">
                    <div className="text-center mb-12">
                        <h2 className="text-2xl md:text-3xl font-black text-text-main dark:text-white mb-4">
                            Available <span className="text-primary">Services</span>
                        </h2>
                        <p className="text-lg text-secondary dark:text-slate-400 max-w-2xl mx-auto">
                            Complete list of our healthcare services with transparent pricing and estimated duration.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {services?.map((service) => {
                            const categoryStyle = getCategoryStyle(service.category || {});

                            return (
                                <div
                                    key={service.id}
                                    className="group bg-white dark:bg-slate-900 p-5 rounded-2xl border border-border-color dark:border-slate-800 shadow-sm hover:shadow-card-hover hover:border-primary/30 transition-all duration-300 hover:-translate-y-1"
                                >
                                    <div className="mb-4">
                                        <span className={`text-xs font-bold px-3 py-1 rounded-full ${categoryStyle.bg} ${categoryStyle.text}`}>
                                            {service.category?.name || 'General'}
                                        </span>
                                    </div>

                                    <h4 className="text-lg font-bold text-text-main dark:text-white mb-3 leading-tight">
                                        {service.name}
                                    </h4>

                                    <p className="text-sm text-secondary dark:text-slate-400 mb-5 leading-relaxed">
                                        {service.description}
                                    </p>

                                    <div className="flex items-center justify-between mb-5 p-4 bg-base-200/50 dark:bg-slate-800/50 rounded-xl">
                                        <div>
                                            <p className="text-xs text-secondary dark:text-slate-400 mb-1">Price</p>
                                            <p className="text-xl font-black text-primary">
                                                {formatPrice(service.price)}
                                            </p>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-xs text-secondary dark:text-slate-400 mb-1">Duration</p>
                                            <p className="text-sm font-bold text-text-main dark:text-white flex items-center gap-1 justify-end">
                                                <FaClock className="text-xs" /> {service.approx_duration}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            <section className="py-10 px-6">
                <div className="">
                    <div className="bg-primary rounded-[2rem] p-12 md:p-16 relative overflow-hidden shadow-2xl">
                        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(255,255,255,0.15)_0%,transparent_70%)]"></div>

                        <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-12">
                            <div className="text-center lg:text-left max-w-2xl">
                                <h2 className="text-3xl md:text-5xl font-black text-white mb-6">
                                    Have Questions About <br />Our Services?
                                </h2>
                                <p className="text-white/80 text-lg mb-10 max-w-2xl mx-auto lg:mx-0">
                                    Our team is ready to help you find the right care for your needs. Reach out anytime.
                                </p>
                                <div className="flex flex-col sm:flex-row gap-5 justify-center lg:justify-start">
                                    <Link
                                        to="/contact"
                                        className="h-14 px-10 bg-white text-primary font-bold text-lg rounded-xl hover:bg-slate-50 transition-all shadow-xl hover:shadow-2xl hover:-translate-y-1 flex items-center justify-center gap-2 group"
                                    >
                                        Contact Us
                                        <FaArrowRight className="group-hover:translate-x-1 transition-transform" />
                                    </Link>
                                    <Link
                                        to="/about"
                                        className="h-14 px-10 bg-primary/20 backdrop-blur-sm border-2 border-white/30 text-white font-bold text-lg rounded-xl hover:bg-primary/30 transition-all flex items-center justify-center"
                                    >
                                        Learn More
                                    </Link>
                                </div>
                            </div>

                            <div className="relative flex-shrink-0">
                                <div className="absolute inset-0 bg-white/10 rounded-full blur-3xl" />
                                <div className="relative size-32 md:size-48 lg:size-64 rounded-full border-[8px] md:border-[12px] lg:border-[16px] border-white/10 flex items-center justify-center mx-auto lg:mx-0">
                                    <FaHeartbeat className="text-4xl md:text-6xl lg:text-[100px] text-white/20" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>


        </div>
    )
}

export default ServicesPage